import React, { useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Select, Alert, Spin, Form, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import InsuranceTable from 'components/PPAOnboarding/InsuranceTable';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { EServiceType, InsuranceSetting, ServerKlarityService } from 'types/common';
import useGetPPAServicePriceConfig from 'hooks/useGetPPAServicePriceConfig';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import { INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE, INSURANCE_TYPE_INSURANCE } from 'constants/common';
import { IPPAServicePriceConfigResponse } from 'types/ppaOnboarding';
import { saveProviderKlarityService } from 'api/klarityService';
import { useSwitchedProvider } from 'pages/ReviewProviderProfilePage/SwitchedProviderContext';
import s from './s.module.less';

export type ServiceTemplateStepRef = { submit: () => Promise<boolean> };

interface StateOption {
    value: string;
    label: string;
}

interface ServiceFormValue {
    states: string[];
    serviceData?: ServerKlarityService;
}

type InitialInsuranceSettings = Record<
    string,
    InsuranceSetting & {
        duration?: number;
        cashPrice?: number;
        insurancePay?: boolean;
        cashPay?: boolean;
    }
>;

interface ServiceFormProps {
    stateOptions: StateOption[];
    servicePriceConfig?: IPPAServicePriceConfigResponse[];
    paymentMethod?:
        | typeof INSURANCE_TYPE_INSURANCE
        | typeof INSURANCE_TYPE_CASH
        | typeof INSURANCE_TYPE_CASH_AND_INSURANCE;
    selectedSpecialty?: string;
    value?: ServiceFormValue;
    onChange?: (value: ServiceFormValue) => void;
    initialInsuranceSettings?: InitialInsuranceSettings;
}

const ServiceForm = React.forwardRef<
    { getServerKlarityServiceData: () => ServerKlarityService | null },
    ServiceFormProps
>(
    (
        {
            stateOptions,
            servicePriceConfig,
            paymentMethod,
            selectedSpecialty,
            value,
            onChange,
            initialInsuranceSettings,
        },
        ref,
    ) => {
        const insuranceTableRef = React.useRef<{ getServerKlarityServiceData: () => ServerKlarityService }>();
        // false on mount so InsuranceTable seeds carriers from initialInsuranceSettings; flips true once
        // the user edits states so their in-progress changes aren't overwritten (mirrors PPA Step4).
        const [isEdit, setIsEdit] = React.useState(false);

        const handleStateChange = (states: string[]) => {
            setIsEdit(true);
            onChange?.({
                states,
                serviceData: value?.serviceData,
            });
        };

        React.useImperativeHandle(ref, () => ({
            getServerKlarityServiceData: () => {
                try {
                    return insuranceTableRef.current?.getServerKlarityServiceData() || null;
                } catch (error) {
                    console.error(error);
                    return null;
                }
            },
        }));

        return (
            <>
                <div className={s.stateSelector}>
                    <div className={s.selectorLabel}>
                        Service states <span className={s.required}>*</span>
                    </div>
                    <Select
                        mode="multiple"
                        value={value?.states}
                        onChange={handleStateChange}
                        options={stateOptions}
                        className={s.stateSelect}
                        placeholder="Select states"
                        maxTagCount="responsive"
                        removeIcon={<CloseOutlined />}
                        suffixIcon={<span className={s.dropdownIcon}>&#x25BE;</span>}
                    />
                </div>

                <div className={s.tableSection}>
                    <div className={s.tableHeader}>
                        <div className={s.tableTitle}>
                            Appointment duration & payments <span className={s.required}>*</span>
                        </div>
                        <div className={s.tableDesc}>
                            Configure visit duration, insurance coverage, and cash pay pricing. Cash pay rates include
                            suggestions and can be updated every 30 days.
                        </div>
                    </div>
                    <InsuranceTable
                        ref={(r) => {
                            if (r) {
                                insuranceTableRef.current = r;
                            }
                        }}
                        isEdit={isEdit}
                        states={value?.states}
                        servicePriceConfig={servicePriceConfig}
                        className={s.insuranceTable}
                        paymentType={paymentMethod}
                        selectedSpecialty={selectedSpecialty}
                        initialInsuranceSettings={initialInsuranceSettings}
                    />
                </div>
            </>
        );
    },
);

interface Props {
    formId?: string;
    paymentMethod?: string;
    selectedSpecialty?: string;
}

const ServiceTemplateStep = forwardRef<ServiceTemplateStepRef, Props>(
    ({ formId, paymentMethod, selectedSpecialty }, ref) => {
        const [form] = Form.useForm();
        const { scheduleToken } = useSwitchedProvider();
        const [allStates] = useGetSettingData(EnumFields.STATE);
        const [servicePriceConfig, priceConfigLoading] = useGetPPAServicePriceConfig();
        // saved services for the created provider, used to restore the selection on Back
        const [savedServiceData, savedServiceLoading] = useProviderKlarityServiceList(
            false,
            undefined,
            false,
            scheduleToken,
        );
        const serviceFormRef = React.useRef<{ getServerKlarityServiceData: () => ServerKlarityService | null }>();

        const stateOptions: StateOption[] = useMemo(() => {
            if (!allStates) return [];
            return allStates.map((state) => ({
                value: state.dictKey,
                label: state.content,
            }));
        }, [allStates]);

        // The saved INIT services for the selected specialty (mirrors PPA SetupService Step4).
        const tableData = useMemo(
            () =>
                savedServiceData?.filter(
                    (item) => item.serviceType === EServiceType.INIT && item.serviceSpecialtyName === selectedSpecialty,
                ),
            [savedServiceData, selectedSpecialty],
        );

        const initialInsuranceSettings = useMemo<InitialInsuranceSettings>(() => {
            if (!tableData) return {};
            return tableData.reduce((acc: InitialInsuranceSettings, service) => {
                const carriers = Array.from(new Set(service.insurances?.map((item) => item.carrier)));
                acc[service.state] = {
                    state: service.state,
                    otherCarriers: '',
                    carriers: carriers.map((carrier) => ({
                        name: carrier,
                        plans:
                            service.insurances
                                ?.filter((item) => item.carrier === carrier)
                                ?.map((item) => item.plan || '') || [],
                        otherPlans: '',
                    })),
                    duration: service.duration,
                    cashPrice: service.patientCost,
                    cashPay: !!service.cashPay,
                    insurancePay: !!service.insurancePay,
                };
                return acc;
            }, {});
        }, [tableData]);

        const hasSavedService = Boolean(tableData && tableData.length > 0);

        // Restore previously-saved states when returning to this step.
        useEffect(() => {
            if (hasSavedService && tableData) {
                form.setFieldsValue({ serviceForm: { states: tableData.map((item) => item.state) } });
            }
        }, [hasSavedService, tableData, form]);

        // Persist the configured service to the created provider; resolves false when validation/save fails.
        useImperativeHandle(ref, () => ({
            submit: async () => {
                let values: { serviceForm: ServiceFormValue };
                try {
                    values = await form.validateFields();
                } catch {
                    return false;
                }
                const serviceData = serviceFormRef.current?.getServerKlarityServiceData();
                if (!serviceData) {
                    message.error('Please complete the service details');
                    return false;
                }
                const states = values.serviceForm?.states || [];
                const finalServiceData = {
                    ...serviceData,
                    // Preserve the existing serviceId so a saved service is updated, not duplicated,
                    // when the user returns to this step and re-submits (mirrors PPA onboarding Step4).
                    serviceId: tableData?.[0]?.serviceId || null,
                    serviceStateItems: serviceData.serviceStateItems.filter((item) => states.includes(item.state)),
                    insurances: serviceData.insurances,
                };
                const res = await saveProviderKlarityService([finalServiceData], [], [], scheduleToken);
                if (res?.error) {
                    message.error(res.error || 'Failed to save services');
                    return false;
                }
                return true;
            },
        }));

        return (
            <div className={s.wrap}>
                <Spin spinning={priceConfigLoading || savedServiceLoading}>
                    <div className={s.content}>
                        <h1 className={s.heading}>
                            Set up your initial visit details{selectedSpecialty ? ` for ${selectedSpecialty}` : ''}
                        </h1>
                        <p className={s.description}>
                            Choose the states where you&apos;ll offer services and set your price.
                        </p>

                        <Alert
                            icon={
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                                        fill="#60A5FA"
                                    />
                                </svg>
                            }
                            description="Grow your potential patient reach by adding more states, carriers, and plans."
                            type="info"
                            showIcon
                            className={s.infoAlert}
                        />

                        <Form
                            form={form}
                            id={formId}
                            initialValues={{ serviceForm: { states: [], serviceData: null } }}
                        >
                            <Form.Item
                                name="serviceForm"
                                noStyle
                                rules={[
                                    {
                                        validator: async (_, val: ServiceFormValue) => {
                                            if (!val?.states || val.states.length === 0) {
                                                message.error('Please select at least one state');
                                                throw new Error('Please select at least one state');
                                            }
                                        },
                                    },
                                ]}
                            >
                                <ServiceForm
                                    stateOptions={stateOptions}
                                    servicePriceConfig={servicePriceConfig}
                                    paymentMethod={paymentMethod as any}
                                    selectedSpecialty={selectedSpecialty}
                                    initialInsuranceSettings={initialInsuranceSettings}
                                    ref={serviceFormRef}
                                />
                            </Form.Item>
                        </Form>
                    </div>
                </Spin>
            </div>
        );
    },
);

export default ServiceTemplateStep;
